import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MapIcon from '@mui/icons-material/Map';
import { Button, colors, IconButton, Stack, Tooltip } from '@mui/material';
import { GridColDef, GridRenderEditCellParams } from '@mui/x-data-grid-premium';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { format } from 'date-fns-tz';
import CandidateCompanySelector from 'src/components/selectors/CandidateCompanySelector';
import CarrierCompanySelector from 'src/components/selectors/CarrierCompanySelector';
import DriverNameSelector from 'src/components/selectors/DriverNameSelector';
import HierarcheyLevelSelector from 'src/components/selectors/HierarcheyLevelSelector';
import PartnerCompanySelector from 'src/components/selectors/PartnerCompanySelector';
import StatusSelector from 'src/components/selectors/StatusSelector';
import datetimeDecorator from 'src/decorators/datetime.decorator';
import numberDecorator from 'src/decorators/number.decorator';
import { PartnerCompanyEntity } from 'src/entities/PartnerCompanyEntity';
import { PlanningsDriverEntity } from 'src/entities/PlanningsDriver.entity';
import { SalesOrderCandidatePartnerCompany, SalesOrderResponseEntity } from 'src/entities/SalesOrder.response.entity';
import arrayUtil from 'src/utils/array.util';
import { formatRecurrence } from 'src/utils/recurrenceFormatter';

import { dataGridUtil } from '../../utils/dataGrid.util';

import ApplyStatus from './ApplyStatus';

export const Columns: (
  openLoadingMapDialog: (orderEntity: SalesOrderResponseEntity) => void,
  openUnloadingMapDialog: (orderEntity: SalesOrderResponseEntity) => void,
  localStorageItemPrefix: string,
  partnerCompanyData: PartnerCompanyEntity[],
  updateEntity: (entity: SalesOrderResponseEntity, onSuccess?: () => void,) => void,
  openEdit: (entity: SalesOrderResponseEntity) => void,
  driverEntities: PlanningsDriverEntity[],
  apiRef: React.MutableRefObject<GridApiPremium>,
) => GridColDef<SalesOrderResponseEntity>[] = (
  openLoadingMapDialog,
  openUnloadingMapDialog,
  localStorageItemPrefix,
  partnerCompanyData,
  updateEntity,
  openEdit,
  driverEntities,
  apiRef,
) => {
  const getWidth = (name: string, width: number) => {
    const maybeWidth: number = dataGridUtil.getSavedColumnWidth(localStorageItemPrefix, name);

    return maybeWidth || width;
  };

  return ([
    {
      field: 'edit',
      headerName: '編集',
      disableExport: true,
      renderCell: (params: { row: SalesOrderResponseEntity }) => (
        <IconButton onClick={() => openEdit(params.row)}>
          <EditOutlinedIcon />
        </IconButton>
      ),
      flex: 2,
      sortable: false,
      filterable: false,
    },
    {
      field: 'companyName',
      disableExport: true,
      headerName: '事業所',
      width: 200,
      filterable: false,
      groupable: false,
    },
    {
      field: 'statusJa',
      headerName: 'ステータス',
      width: 200,
      renderCell: (params: { row: SalesOrderResponseEntity }) => (
        <StatusSelector
          status={params.row.status}
          entity={params.row}
          updateEntity={updateEntity}
        />
      ),
    },
    {
      field: 'candidateCompanies',
      headerName: '依頼候補企業',
      width: 200,
      valueGetter: (params: { row: SalesOrderResponseEntity }) => params.row.salesOrderCandidatePartnerCompaniesAttributes?.map((it) => it.name)?.join(','),
      renderCell: (params: { row: SalesOrderResponseEntity, value: string }) => {
        const handleConfirm = (partners: SalesOrderCandidatePartnerCompany[]) => {
          const entity = params.row;
          const newIds = partners.map((it) => it.partnerCompanyId);
          const removed = entity.salesOrderCandidatePartnerCompaniesAttributes?.filter(
            (it) => !newIds.includes(it.partnerCompanyId)
          ).map((it) => ({ ...it, _destroy: true }));
          updateEntity({
            ...entity,
            salesOrderCandidatePartnerCompaniesAttributes: [...removed, ...partners],
          });
        };

        return (
          <CandidateCompanySelector
            selectedPartners={params.row.salesOrderCandidatePartnerCompaniesAttributes || []}
            partners={partnerCompanyData}
            onConfirm={handleConfirm}
            renderTrigger={(handleOpen) => (
              <Button
                size="small"
                onClick={handleOpen}
              >
                {params.value || '依頼候補企業を選択'}
              </Button>
            )}
          />
        );
      },
    },
    {
      field: 'fixedCompany',
      headerName: '確定企業',
      width: 200,
      valueGetter: (params: { row: SalesOrderResponseEntity }) => params.row.partnerCompanyName,
      renderCell: (params: { row: SalesOrderResponseEntity }) => (
        <PartnerCompanySelector
          selectedCompanyId={params.row.partnerCompanyId}
          selectedCompanyName={params.row.partnerCompanyName}
          partners={partnerCompanyData}
          buttonLabel={params.row.partnerCompanyName}
          onConfirm={(companyId, companyName) => {
            // ここで更新APIを呼び出す処理を実装
            updateEntity({
              ...params.row,
              partnerCompanyId: companyId,
              partnerCompanyName: companyName,
            });
          }}
        />
      ),
    },
    {
      field: 'applyToOrders',
      headerName: '案件(配車)管理に反映',
      width: 200,
      valueGetter: (params: { row: SalesOrderResponseEntity }) => {
        if (params.row.applyStatus === 'ONGOING') { return '反映中'; }
        if (params.row.applyStatus === 'FAILED') { return '反映失敗'; }
        if (params.row.applyStatus === 'COMPLETED') { return '反映済み'; }
        return '';
      },
      renderCell: (params: { row: SalesOrderResponseEntity, value: string }) => <ApplyStatus value={params.value} entity={params.row} />,
    },
    {
      field: 'truckNumber',
      headerName: '車番',
      type: 'string' as const,
      width: 200,
      editable: true,
    },
    {
      field: 'driverName',
      headerName: 'ドライバー',
      width: 200,
      editable: true,
      valueGetter: (params: { row: SalesOrderResponseEntity }) => params.row.driverName,
      renderEditCell: (params: GridRenderEditCellParams<SalesOrderResponseEntity>) => (
        <DriverNameSelector
          driverEntities={driverEntities.map((it) => ({ name: it.name, temporary: it.temporary }))}
          value={params.row.driverName}
          id={params.row.id}
          updateDriverName={(_id, value) => {
            updateEntity({
              ...params.row,
              driverName: value,
            });
            // eslint-disable-next-line no-void
            void apiRef.current.setEditCellValue({ id: params.id, field: params.field, value, });
          }}
          forceFocus
          withLabel={false}
        />
      ),
    },
    {
      field: 'carrierCompany',
      headerName: '運送会社',
      width: 200,
      editable: true,
      renderEditCell: (params: GridRenderEditCellParams<SalesOrderResponseEntity, string>) => (
        <CarrierCompanySelector
          partnerCompanyEntities={partnerCompanyData}
          value={params.value}
          id={params.row.id}
          update={(_id, value) => {
            updateEntity({
              ...params.row,
              carrierCompany: value,
            });
            // eslint-disable-next-line no-void
            void apiRef.current.setEditCellValue({ id: params.id, field: params.field, value, });
          }}
        />
      ),
    },
    {
      field: 'operatingCarrier',
      headerName: '実走行会社',
      width: 200,
      editable: true,
      renderEditCell: (params: GridRenderEditCellParams<SalesOrderResponseEntity, string>) => (
        <CarrierCompanySelector
          partnerCompanyEntities={partnerCompanyData}
          value={params.value}
          id={params.row.id}
          update={(_id, value) => {
            updateEntity({
              ...params.row,
              operatingCarrier: value,
            });
            // eslint-disable-next-line no-void
            void apiRef.current.setEditCellValue({ id: params.id, field: params.field, value, });
          }}
        />
      ),
    },
    {
      field: 'hierarcheyLevel',
      headerName: '請負階層',
      width: 100,
      valueGetter: (params: { row: SalesOrderResponseEntity }) => {
        if (!params.row.hierarcheyLevel) return '';

        const value = String(params.row.hierarcheyLevel);
        const newValue = value.endsWith('次請け') ? parseInt(value.toString().charAt(0), 10) : value;

        return `${newValue}次請け`;
      },
      editable: true,
      renderEditCell: (params: GridRenderEditCellParams<SalesOrderResponseEntity, string>) => (
        <HierarcheyLevelSelector
          value={params.value}
          id={params.row.id}
          update={(_id, value) => {
            updateEntity({
              ...params.row,
              hierarcheyLevel: value ? Number(value) : '',
            });
            const newValue = value ? `${value}次請け` : null;
            // eslint-disable-next-line no-void
            void apiRef.current.setEditCellValue({ id: params.id, field: params.field, value: newValue, });
          }}
        />
      ),
    },
    {
      field: 'recurrenceRule',
      headerName: '頻度',
      width: 200,
      valueGetter: (params: { row: SalesOrderResponseEntity }) => {
        const rule = params.row.salesOrderRecurrenceRuleAttributes;
        return formatRecurrence(rule);
      },
    },
    {
      field: 'registerdLicenseName',
      headerName: '登録者',
      width: 200,
      valueGetter: (params: { row: SalesOrderResponseEntity }) => {
        const license = params.row.registeredLicense;
        if (!license) return '';

        return license.personName || license.email;
      },
      groupable: false,
    },
    {
      field: 'encodedId',
      headerName: 'ID',
      groupable: false,
    },
    {
      field: 'code',
      headerName: '注文コード',
      width: 200,
      groupable: false,
    },
    {
      field: 'shipperName',
      headerName: '荷主名',
      width: 200
    },
    {
      field: 'shipperContactPerson',
      headerName: '荷主担当者',
      width: 200,
      groupable: false,
    },
    {
      field: 'shipperPhoneNumber',
      headerName: '荷主電話番号',
      width: 200,
      groupable: false,
    },
    {
      field: 'shipperEmailAddress',
      headerName: '荷主メールアドレス',
      width: 200,
      groupable: false,
    },
    {
      field: 'loadingName',
      headerName: '積地',
      width: 300
    },
    {
      field: 'loadingAddress',
      headerName: '積地住所',
      width: 500
    },
    {
      field: 'loadingPosition',
      headerName: '積地座標',
      disableExport: true,
      width: 250,
      valueGetter: (params: { row: SalesOrderResponseEntity }) => {
        if (params.row.loadingLatitude && params.row.loadingLongitude) {
          return `${params.row.loadingLatitude}, ${params.row.loadingLongitude}`;
        }
        return '';
      },
      renderCell: (params: { row: SalesOrderResponseEntity }) => {
        if (params.row.loadingLatitude && params.row.loadingLongitude) {
          return (
            <Stack direction="row" alignContent="center" alignItems="center">
              <Tooltip title="位置を調整します">
                <IconButton onClick={() => openLoadingMapDialog(params.row)}>
                  <MapIcon />
                </IconButton>
              </Tooltip>
              {[params.row.loadingLatitude, params.row.loadingLongitude].join(', ')}
            </Stack>
          );
        }
        return '';
      },
      groupable: false,
    },
    {
      field: 'loadingLatitude',
      headerName: '積地緯度',
      filterable: false,
      sortable: false,
      groupable: false,
    },
    {
      field: 'loadingLongitude',
      headerName: '積地経度',
      filterable: false,
      sortable: false,
      groupable: false,
    },
    {
      field: 'salesOrderLoadingSpecifiedDatetimesStartDate',
      headerName: '積日',
      width: 200,
      type: 'string' as const,
      valueGetter: (params: { row: SalesOrderResponseEntity }) => {
        const times = params.row.salesOrderLoadingSpecifiedDatetimesAttributes;
        if (!times || times.length === 0) { return ''; }

        const target = times[0];

        return format(new Date(target.start_at), 'yyyy/MM/dd', { timeZone: 'Asia/Tokyo' });
      },
      groupable: false,
    },
    {
      field: 'loadingStartAt1',
      headerName: '積地受付',
      width: 80,
      valueGetter: (params: { row: SalesOrderResponseEntity }) => {
        const times = params.row.salesOrderLoadingSpecifiedDatetimesAttributes;
        if (!times || times.length === 0) { return ''; }

        const target = times[0];

        return format(new Date(target.start_at), 'HH:mm', { timeZone: 'Asia/Tokyo' });
      },
      filterable: false,
      sortable: false,
      groupable: false,
    },
    {
      field: 'loadingEndAt1',
      headerName: '積地締切',
      width: 80,
      valueGetter: (params: { row: SalesOrderResponseEntity }) => {
        const times = params.row.salesOrderLoadingSpecifiedDatetimesAttributes;
        if (!times || times.length === 0) { return ''; }

        const target = times[0];
        const startDate = datetimeDecorator.toYyyyMmDd(new Date(target.start_at), '/');
        const endDate = datetimeDecorator.toYyyyMmDd(new Date(target.end_at), '/');
        const fmt = startDate === endDate ? 'HH:mm' : 'yyyy/MM/dd HH:mm';

        return format(new Date(target.end_at), fmt, { timeZone: 'Asia/Tokyo' });
      },
      filterable: false,
      sortable: false,
      groupable: false,
    },
    {
      field: 'loadingStartAt2',
      headerName: '積地受付②',
      width: 80,
      valueGetter: (params: { row: SalesOrderResponseEntity }) => {
        const times = params.row.salesOrderLoadingSpecifiedDatetimesAttributes;
        if (!times || times.length < 2) { return ''; }

        const base = times[0];
        const target = times[1];
        const baseDate = datetimeDecorator.toYyyyMmDd(new Date(base.start_at), '/');
        const startDate = datetimeDecorator.toYyyyMmDd(new Date(target.start_at), '/');
        const fmt = baseDate === startDate ? 'HH:mm' : 'yyyy/MM/dd HH:mm';

        return format(new Date(target.start_at), fmt, { timeZone: 'Asia/Tokyo' });
      },
      filterable: false,
      sortable: false,
      groupable: false,
    },
    {
      field: 'loadingEndAt2',
      headerName: '積地締切②',
      width: 80,
      valueGetter: (params: { row: SalesOrderResponseEntity }) => {
        const times = params.row.salesOrderLoadingSpecifiedDatetimesAttributes;
        if (!times || times.length < 2) { return ''; }

        const base = times[0];
        const target = times[1];
        const baseDate = datetimeDecorator.toYyyyMmDd(new Date(base.start_at), '/');
        const endDate = datetimeDecorator.toYyyyMmDd(new Date(target.end_at), '/');
        const fmt = baseDate === endDate ? 'HH:mm' : 'yyyy/MM/dd HH:mm';

        return format(new Date(target.end_at), fmt, { timeZone: 'Asia/Tokyo' });
      },
      filterable: false,
      sortable: false,
      groupable: false,
    },
    {
      field: 'loadingStartAt3',
      headerName: '積地受付③',
      width: 80,
      valueGetter: (params: { row: SalesOrderResponseEntity }) => {
        const times = params.row.salesOrderLoadingSpecifiedDatetimesAttributes;
        if (!times || times.length < 3) { return ''; }

        const base = times[0];
        const target = times[2];
        const baseDate = datetimeDecorator.toYyyyMmDd(new Date(base.start_at), '/');
        const startDate = datetimeDecorator.toYyyyMmDd(new Date(target.start_at), '/');
        const fmt = baseDate === startDate ? 'HH:mm' : 'yyyy/MM/dd HH:mm';

        return format(new Date(target.start_at), fmt, { timeZone: 'Asia/Tokyo' });
      },
      filterable: false,
      sortable: false,
      groupable: false,
    },
    {
      field: 'loadingEndAt3',
      headerName: '積地締切③',
      width: 80,
      valueGetter: (params: { row: SalesOrderResponseEntity }) => {
        const times = params.row.salesOrderLoadingSpecifiedDatetimesAttributes;
        if (!times || times.length < 3) { return ''; }

        const base = times[0];
        const target = times[2];
        const baseDate = datetimeDecorator.toYyyyMmDd(new Date(base.start_at), '/');
        const endDate = datetimeDecorator.toYyyyMmDd(new Date(target.end_at), '/');
        const fmt = baseDate === endDate ? 'HH:mm' : 'yyyy/MM/dd HH:mm';

        return format(new Date(target.end_at), fmt, { timeZone: 'Asia/Tokyo' });
      },
      filterable: false,
      sortable: false,
      groupable: false,
    },
    {
      field: 'loadingStayingMinutes',
      headerName: '積地作業分数',
      width: 80,
      type: 'number' as const,
      groupable: false,
    },
    {
      field: 'unloadingName',
      headerName: '降地名称',
      width: 300,
    },
    {
      field: 'unloadingAddress',
      headerName: '降地住所',
      width: 500,
    },
    {
      field: 'unloadingPosition',
      headerName: '降地座標',
      disableExport: true,
      width: 250,
      valueGetter: (params: { row: SalesOrderResponseEntity }) => {
        if (params.row.unloadingLatitude && params.row.unloadingLongitude) {
          return `${params.row.unloadingLatitude}, ${params.row.unloadingLongitude}`;
        }
        return '';
      },
      renderCell: (params: { row: SalesOrderResponseEntity }) => {
        if (params.row.unloadingLatitude && params.row.unloadingLongitude) {
          return (
            <Stack direction="row" alignContent="center" alignItems="center">
              <Tooltip title="位置を調整します">
                <IconButton onClick={() => openUnloadingMapDialog(params.row)}>
                  <MapIcon />
                </IconButton>
              </Tooltip>
              {[params.row.unloadingLatitude, params.row.unloadingLongitude].join(', ')}
            </Stack>
          );
        }
        return '';
      },
      groupable: false,
    },
    {
      field: 'unloadingLatitude',
      headerName: '降地緯度',
      filterable: false,
      sortable: false,
      groupable: false,
    },
    {
      field: 'unloadingLongitude',
      headerName: '降地経度',
      filterable: false,
      sortable: false,
      groupable: false,
    },
    {
      field: 'salesOrderUnloadingSpecifiedDatetimesStartDate',
      headerName: '降日',
      width: 200,
      type: 'string' as const,
      valueGetter: (params: { row: SalesOrderResponseEntity }) => {
        const times = params.row.salesOrderUnloadingSpecifiedDatetimesAttributes;
        if (!times || times.length < 1) { return ''; }

        const target = times[0];

        return format(new Date(target.start_at), 'yyyy/MM/dd', { timeZone: 'Asia/Tokyo' });
      },
      groupable: false,
    },
    {
      field: 'unloadingStartAt',
      headerName: '降地受付',
      width: 80,
      valueGetter: (params: { row: SalesOrderResponseEntity }) => {
        const times = params.row.salesOrderUnloadingSpecifiedDatetimesAttributes;
        if (!times || times.length < 1) { return ''; }

        const target = times[0];

        return format(new Date(target.start_at), 'HH:mm', { timeZone: 'Asia/Tokyo' });
      },
      filterable: false,
      sortable: false,
      groupable: false,
    },
    {
      field: 'unloadingEndAt',
      headerName: '降地締切',
      width: 80,
      valueGetter: (params: { row: SalesOrderResponseEntity }) => {
        const times = params.row.salesOrderUnloadingSpecifiedDatetimesAttributes;
        if (!times || times.length < 1) { return ''; }

        const target = times[0];
        const startDate = datetimeDecorator.toYyyyMmDd(new Date(target.start_at), '/');
        const endDate = datetimeDecorator.toYyyyMmDd(new Date(target.end_at), '/');
        const fmt = startDate === endDate ? 'HH:mm' : 'yyyy/MM/dd HH:mm';

        return format(new Date(target.end_at), fmt, { timeZone: 'Asia/Tokyo' });
      },
      filterable: false,
      sortable: false,
      groupable: false,
    },
    {
      field: 'unloadingStartAt2',
      headerName: '降地受付②',
      width: 80,
      valueGetter: (params: { row: SalesOrderResponseEntity }) => {
        const times = params.row.salesOrderUnloadingSpecifiedDatetimesAttributes;
        if (!times || times.length < 2) { return ''; }

        const base = times[0];
        const target = times[1];
        const baseDate = datetimeDecorator.toYyyyMmDd(new Date(base.start_at), '/');
        const startDate = datetimeDecorator.toYyyyMmDd(new Date(target.start_at), '/');
        const fmt = baseDate === startDate ? 'HH:mm' : 'yyyy/MM/dd HH:mm';

        return format(new Date(target.start_at), fmt, { timeZone: 'Asia/Tokyo' });
      },
      filterable: false,
      sortable: false,
      groupable: false,
    },
    {
      field: 'unloadingEndAt2',
      headerName: '降地締切②',
      width: 80,
      valueGetter: (params: { row: SalesOrderResponseEntity }) => {
        const times = params.row.salesOrderUnloadingSpecifiedDatetimesAttributes;
        if (!times || times.length < 2) { return ''; }

        const base = times[0];
        const target = times[1];
        const baseDate = datetimeDecorator.toYyyyMmDd(new Date(base.start_at), '/');
        const endDate = datetimeDecorator.toYyyyMmDd(new Date(target.end_at), '/');
        const fmt = baseDate === endDate ? 'HH:mm' : 'yyyy/MM/dd HH:mm';

        return format(new Date(target.end_at), fmt, { timeZone: 'Asia/Tokyo' });
      },
      filterable: false,
      sortable: false,
      groupable: false,
    },
    {
      field: 'unloadingStartAt3',
      headerName: '降地受付③',
      width: 80,
      valueGetter: (params: { row: SalesOrderResponseEntity }) => {
        const times = params.row.salesOrderUnloadingSpecifiedDatetimesAttributes;
        if (!times || times.length < 3) { return ''; }

        const base = times[0];
        const target = times[2];
        const baseDate = datetimeDecorator.toYyyyMmDd(new Date(base.start_at), '/');
        const startDate = datetimeDecorator.toYyyyMmDd(new Date(target.start_at), '/');
        const fmt = baseDate === startDate ? 'HH:mm' : 'yyyy/MM/dd HH:mm';

        return format(new Date(target.start_at), fmt, { timeZone: 'Asia/Tokyo' });
      },
      filterable: false,
      sortable: false,
      groupable: false,
    },
    {
      field: 'unloadingEndAt3',
      headerName: '降地締切③',
      width: 80,
      valueGetter: (params: { row: SalesOrderResponseEntity }) => {
        const times = params.row.salesOrderUnloadingSpecifiedDatetimesAttributes;
        if (!times || times.length < 3) { return ''; }

        const base = times[0];
        const target = times[2];
        const baseDate = datetimeDecorator.toYyyyMmDd(new Date(base.start_at), '/');
        const endDate = datetimeDecorator.toYyyyMmDd(new Date(target.end_at), '/');
        const fmt = baseDate === endDate ? 'HH:mm' : 'yyyy/MM/dd HH:mm';

        return format(new Date(target.end_at), fmt, { timeZone: 'Asia/Tokyo' });
      },
      filterable: false,
      sortable: false,
      groupable: false,
    },
    {
      field: 'unloadingStayingMinutes',
      headerName: '降地作業分数',
      width: 80,
      type: 'number' as const,
      groupable: false,
    },
    {
      field: 'itemCount',
      headerName: '数量',
      width: 80,
      type: 'number' as const,
      groupable: false,
    },
    {
      field: 'itemPackingStyle',
      headerName: '荷姿',
      width: 80,
      groupable: false,
    },
    {
      field: 'itemTotalWeightKg',
      headerName: '総重量',
      width: 80,
      type: 'number' as const,
      valueFormatter: (value: number) => {
        if (!value && value !== 0) return '';
        if (value === 0) return '0kg';

        return numberDecorator.toRoundedUnit(value, 'kg', 1, '');
      },
      groupable: false,
    },
    {
      field: 'itemTotalVolumeM3',
      headerName: '総体積',
      width: 80,
      type: 'number' as const,
      valueFormatter: (value: number) => {
        if (!value) return '';

        return numberDecorator.toRoundedUnit(value, 'm³', 10, '');
      },
      groupable: false,
    },
    {
      field: 'itemName',
      headerName: '品名',
      width: 80,
      groupable: false,
    },
    {
      field: 'itemKlass',
      headerName: '輸送区分',
      width: 80,
      groupable: false,
    },
    {
      field: 'itemHandlingOfCargoStyle',
      headerName: '荷扱い',
      width: 80,
      groupable: false,
    },
    {
      field: 'itemCanBeMixed',
      headerName: '混載可否',
      valueGetter: (params: { row: SalesOrderResponseEntity }) => (params.row.itemCanBeMixed ? '可' : '不可'),
      renderCell: (params: { row: SalesOrderResponseEntity }) => (
        params.row.itemCanBeMixed
          ? <CheckIcon sx={{ color: colors.grey[500] }} /> : <CloseIcon sx={{ color: colors.grey[500] }} />
      ),
      width: 80,
      type: 'boolean' as const,
      groupable: false,
    },
    {
      field: 'truckCount',
      headerName: '必要台数',
      width: 80,
      type: 'number' as const,
      groupable: false,
    },
    {
      field: 'designatedTruckKlasses',
      headerName: '車両タイプ',
      width: 80,
      valueFormatter: (value: string[]) => { // CSVに出力する場合
        if (!value || value.length < 2) { return value?.join(','); }
        return `"${value.join(',')}"`;
      },
      renderCell: (params: { row: SalesOrderResponseEntity }) => { // 画面に表示する場合
        if (!params.row.designatedTruckKlasses) { return ''; }
        return params.row.designatedTruckKlasses.join(',');
      },
      groupable: false,
    },
    {
      field: 'designatedTruckCarModels',
      headerName: '指定車種',
      width: 80,
      valueFormatter: (value: string[]) => { // CSVに出力する場合
        if (!value || value.length < 2) { return value?.join(','); }
        return `"${value.join(',')}"`;
      },
      renderCell: (params: { row: SalesOrderResponseEntity }) => { // 画面に表示する場合
        if (!params.row.designatedTruckCarModels) { return ''; }
        return params.row.designatedTruckCarModels.join(',');
      },
      groupable: false,
    },
    {
      field: 'designatedTruckLoadingPlatformHeights',
      headerName: '荷台高',
      width: 80,
      valueFormatter: (value: string[]) => { // CSVに出力する場合
        if (!value || value.length < 2) { return value?.join(','); }
        return `"${value.join(',')}"`;
      },
      renderCell: (params: { row: SalesOrderResponseEntity }) => { // 画面に表示する場合
        if (!params.row.designatedTruckLoadingPlatformHeights) { return ''; }
        return params.row.designatedTruckLoadingPlatformHeights.join(',');
      },
      groupable: false,
    },
    {
      field: 'designatedTruckLoadingPlatformWidths',
      headerName: '荷台幅',
      width: 80,
      valueFormatter: (value: string[]) => { // CSVに出力する場合
        if (!value || value.length < 2) { return value?.join(','); }
        return `"${value.join(',')}"`;
      },
      renderCell: (params: { row: SalesOrderResponseEntity }) => { // 画面に表示する場合
        if (!params.row.designatedTruckLoadingPlatformWidths) { return ''; }
        return params.row.designatedTruckLoadingPlatformWidths.join(',');
      },
      groupable: false,
    },
    {
      field: 'designatedTruckLoadingPlatformLengths',
      headerName: '荷台長',
      width: 80,
      valueFormatter: (value: string[]) => { // CSVに出力する場合
        if (!value || value.length < 2) { return value?.join(','); }
        return `"${value.join(',')}"`;
      },
      renderCell: (params: { row: SalesOrderResponseEntity }) => { // 画面に表示する場合
        if (!params.row.designatedTruckLoadingPlatformLengths) { return ''; }
        return params.row.designatedTruckLoadingPlatformLengths.join(',');
      },
      groupable: false,
    },
    {
      field: 'designatedTruckFloorSpecifications',
      headerName: '床仕様',
      width: 80,
      valueFormatter: (value: string[]) => { // CSVに出力する場合
        if (!value || value.length < 2) { return value?.join(','); }
        return `"${value.join(',')}"`;
      },
      renderCell: (params: { row: SalesOrderResponseEntity }) => { // 画面に表示する場合
        if (!params.row.designatedTruckFloorSpecifications) { return ''; }
        return params.row.designatedTruckFloorSpecifications.join(',');
      },
      groupable: false,
    },
    {
      field: 'designatedTruckFeatures',
      headerName: '指定装置・特徴',
      width: 80,
      valueFormatter: (value: string[]) => { // CSVに出力する場合
        if (!value || value.length < 2) { return value?.join(','); }
        return `"${value.join(',')}"`;
      },
      renderCell: (params: { row: SalesOrderResponseEntity }) => { // 画面に表示する場合
        if (!params.row.designatedTruckFeatures) { return ''; }
        return params.row.designatedTruckFeatures.join(',');
      },
      groupable: false,
    },
    {
      field: 'orderBillingAmountTotal',
      headerName: '請求金額合計',
      width: 100,
      type: 'number' as const,
      valueGetter: (params: { row: SalesOrderResponseEntity }) => {
        const amount = params.row.salesOrderBillingAmountAttributes;
        if (!amount) return '';

        const numKeys = Object.keys(amount).filter((it) => !['id', 'ancillary_content'].includes(it));
        const values = numKeys.map((it) => Number(amount[it])).filter((it) => it);
        if (values.length === 0) return '';
        return values.reduce((sum, it) => (sum + (it || 0)));
      },
      groupable: false,
    },
    {
      field: 'orderBillingAmountBasicFeeYen',
      headerName: '請求 基本運賃',
      width: 80,
      type: 'number' as const,
      valueGetter: (params: { row: SalesOrderResponseEntity }) => params.row.salesOrderBillingAmountAttributes?.basicFeeYen,
      groupable: false,
    },
    {
      field: 'orderBillingAmountSurchargeFeeYen',
      headerName: '請求 サーチャージ料',
      width: 80,
      type: 'number' as const,
      valueGetter: (params: { row: SalesOrderResponseEntity, }) => params.row.salesOrderBillingAmountAttributes?.surchargeFeeYen,
      groupable: false,
    },
    {
      field: 'orderBillingAmountRelayFeeYen',
      headerName: '請求 中継料',
      width: 80,
      type: 'number' as const,
      valueGetter: (params: { row: SalesOrderResponseEntity, }) => params.row.salesOrderBillingAmountAttributes?.relayFeeYen,
      groupable: false,
    },
    {
      field: 'orderBillingAmountHighwayFeeYen',
      headerName: '請求 高速代金',
      width: 80,
      type: 'number' as const,
      valueGetter: (params: { row: SalesOrderResponseEntity, }) => params.row.salesOrderBillingAmountAttributes?.highwayFeeYen,
      groupable: false,
    },
    {
      field: 'orderBillingAmountLoadingFeeYen',
      headerName: '請求 積込料金',
      width: 80,
      type: 'number' as const,
      valueGetter: (params: { row: SalesOrderResponseEntity, }) => params.row.salesOrderBillingAmountAttributes?.loadingFeeYen,
      groupable: false,
    },
    {
      field: 'orderBillingAmountAncillaryFeeYen',
      headerName: '請求 付帯作業料金',
      width: 80,
      type: 'number' as const,
      valueGetter: (params: { row: SalesOrderResponseEntity, }) => params.row.salesOrderBillingAmountAttributes?.ancillaryFeeYen,
      groupable: false,
    },
    {
      field: 'orderBillingAmountWaitingTimeFeeYen',
      headerName: '請求 待機料金',
      width: 80,
      type: 'number' as const,
      valueGetter: (params: { row: SalesOrderResponseEntity, }) => params.row.salesOrderBillingAmountAttributes?.waitingTimeFeeYen,
      groupable: false,
    },
    {
      field: 'orderBillingAmountUnloadingFeeYen',
      headerName: '請求 取卸料',
      width: 80,
      type: 'number' as const,
      valueGetter: (params: { row: SalesOrderResponseEntity, }) => params.row.salesOrderBillingAmountAttributes?.unloadingFeeYen,
      groupable: false,
    },
    {
      field: 'orderBillingAmountExpensesFeeYen',
      headerName: '請求 諸経費',
      width: 80,
      type: 'number' as const,
      valueGetter: (params: { row: SalesOrderResponseEntity, }) => params.row.salesOrderBillingAmountAttributes?.expensesFeeYen,
      groupable: false,
    },
    {
      field: 'orderBillingAmountAncillaryContent',
      headerName: '請求 付帯作業備考',
      valueGetter: (params: { row: SalesOrderResponseEntity }) => params.row.salesOrderBillingAmountAttributes?.ancillaryContent,
      renderCell: (params: { row: SalesOrderResponseEntity }) => (
        <Tooltip title={params.row.salesOrderBillingAmountAttributes?.ancillaryContent}>
          <span>
            {params.row.salesOrderBillingAmountAttributes?.ancillaryContent}
          </span>
        </Tooltip>
      ),
      width: 200,
      groupable: false,
    },
    {
      field: 'orderPaymentAmountTotal',
      headerName: '支払金額合計',
      width: 100,
      type: 'number' as const,
      valueGetter: (params: { row: SalesOrderResponseEntity, }) => {
        const amount = params.row.salesOrderPaymentAmountAttributes;
        if (!amount) return '';

        const numKeys = Object.keys(amount).filter((it) => !['id', 'ancillary_content'].includes(it));
        const values = numKeys.map((it) => Number(amount[it])).filter((it) => it);
        if (values.length === 0) return '';
        return values.reduce((sum, it) => (sum + it), 0);
      },
      groupable: false,
    },
    {
      field: 'orderPaymentAmountBasicFeeYen',
      headerName: '支払 基本運賃',
      width: 80,
      type: 'number' as const,
      valueGetter: (params: { row: SalesOrderResponseEntity, }) => params.row.salesOrderPaymentAmountAttributes?.basicFeeYen,
      groupable: false,
    },
    {
      field: 'orderPaymentAmountSurchargeFeeYen',
      headerName: '支払 サーチャージ料',
      width: 80,
      type: 'number' as const,
      valueGetter: (params: { row: SalesOrderResponseEntity, }) => params.row.salesOrderPaymentAmountAttributes?.surchargeFeeYen,
      groupable: false,
    },
    {
      field: 'orderPaymentAmountRelayFeeYen',
      headerName: '支払 中継料',
      width: 80,
      type: 'number' as const,
      valueGetter: (params: { row: SalesOrderResponseEntity, }) => params.row.salesOrderPaymentAmountAttributes?.relayFeeYen,
      groupable: false,
    },
    {
      field: 'orderPaymentAmountHighwayFeeYen',
      headerName: '支払 高速代金',
      width: 80,
      type: 'number' as const,
      valueGetter: (params: { row: SalesOrderResponseEntity, }) => params.row.salesOrderPaymentAmountAttributes?.highwayFeeYen,
      groupable: false,
    },
    {
      field: 'orderPaymentAmountLoadingFeeYen',
      headerName: '支払 積込料金',
      width: 80,
      type: 'number' as const,
      valueGetter: (params: { row: SalesOrderResponseEntity, }) => params.row.salesOrderPaymentAmountAttributes?.loadingFeeYen,
      groupable: false,
    },
    {
      field: 'orderPaymentAmountAncillaryFeeYen',
      headerName: '支払 付帯作業料金',
      width: 80,
      type: 'number' as const,
      valueGetter: (params: { row: SalesOrderResponseEntity, }) => params.row.salesOrderPaymentAmountAttributes?.ancillaryFeeYen,
      groupable: false,
    },
    {
      field: 'orderPaymentAmountWaitingTimeFeeYen',
      headerName: '支払 待機料金',
      width: 80,
      type: 'number' as const,
      valueGetter: (params: { row: SalesOrderResponseEntity, }) => params.row.salesOrderPaymentAmountAttributes?.waitingTimeFeeYen,
      groupable: false,
    },
    {
      field: 'orderPaymentAmountUnloadingFeeYen',
      headerName: '支払 取卸料',
      width: 80,
      type: 'number' as const,
      valueGetter: (params: { row: SalesOrderResponseEntity, }) => params.row.salesOrderPaymentAmountAttributes?.unloadingFeeYen,
      groupable: false,
    },
    {
      field: 'orderPaymentAmountExpensesFeeYen',
      headerName: '支払 諸経費',
      width: 80,
      type: 'number' as const,
      valueGetter: (params: { row: SalesOrderResponseEntity, }) => params.row.salesOrderPaymentAmountAttributes?.expensesFeeYen,
      groupable: false,
    },
    {
      field: 'order_payment_amount_ancillary_content',
      headerName: '支払 付帯作業備考',
      valueGetter: (params: { row: SalesOrderResponseEntity }) => params.row.salesOrderPaymentAmountAttributes?.ancillaryContent,
      renderCell: (params: { row: SalesOrderResponseEntity }) => (
        <Tooltip title={params.row.salesOrderPaymentAmountAttributes?.ancillaryContent}>
          <span>
            {params.row.salesOrderPaymentAmountAttributes?.ancillaryContent}
          </span>
        </Tooltip>
      ),
      width: 200,
      groupable: false,
    },
    {
      field: 'memo',
      headerName: '備考',
      width: 300,
      renderCell: (params: { row: SalesOrderResponseEntity }) => {
        if (!params.row.memo) return '';

        const val = params.row.memo
          .trim()
          .split('\n')
          .flatMap((str, outerIndex) => arrayUtil.sliceByNumber(str.split(''), 20).map((s, idx) => (
            <li
              style={{
                listStyle: 'none'
              }}
              key={['memo', params.row.id, outerIndex, idx].join('-')}
            >
              {s}
            </li>
          )));

        return (
          <ul
            style={{
              padding: 0
            }}
          >
            {val}
          </ul>
        );
      },
      groupable: false,
    },
  ] as GridColDef<SalesOrderResponseEntity>[]).map((it) => ({
    ...it,
    width: getWidth(it.field, it.width || 50)
  }));
};
